import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useIntl } from 'react-intl';

import { type DropdownListItem } from '@verantos-dev/shared-components';
import { DomainType } from '@verantos-dev/types-cohorts';
import { ConceptGroup } from '@verantos-dev/types-concepts';

import { ComboBoxDropdownListItem } from './useCharacteristicsConceptGroupsMapping';

interface UseCustomConceptGroupsDialogProps {
  open: boolean;
  domain: DomainType;
  availableConceptGroups: ComboBoxDropdownListItem[];
  selectedConceptGroups: ComboBoxDropdownListItem[];
  onApplySelection: (selectedGroups: ConceptGroup[]) => void;
  onClose: () => void;
  isLoading?: boolean;
}

export const useCustomConceptGroupsDialog = ({
  open,
  domain,
  availableConceptGroups,
  selectedConceptGroups,
  onApplySelection,
  onClose,
}: UseCustomConceptGroupsDialogProps) => {
  const intl = useIntl();

  const [localSelectedGroups, setLocalSelectedGroups] = useState<ComboBoxDropdownListItem[]>([]);
  const [originalSelectedGroups, setOriginalSelectedGroups] = useState<ComboBoxDropdownListItem[]>(
    []
  );
  const [selectedConceptGroupForDetails, setSelectedConceptGroupForDetails] = useState<
    string | undefined
  >(undefined);

  // Use ref to track previous selectedConceptGroups to prevent infinite loops
  const prevSelectedConceptGroupsRef = useRef<ComboBoxDropdownListItem[]>([]);

  // Deep equality check for selectedConceptGroups
  const selectedConceptGroupsChanged = useMemo(() => {
    const prev = prevSelectedConceptGroupsRef.current;
    if (prev.length !== selectedConceptGroups.length) return true;
    return !prev.every(
      (item, index) =>
        item.value === selectedConceptGroups[index]?.value &&
        item.displayValue === selectedConceptGroups[index]?.displayValue
    );
  }, [selectedConceptGroups]);

  useEffect(() => {
    if (open && selectedConceptGroupsChanged) {
      const selectedWithKeys = selectedConceptGroups.map((item) => ({
        ...item,
        key: item.value,
      }));
      setLocalSelectedGroups(selectedWithKeys);
      setOriginalSelectedGroups(selectedWithKeys);
      prevSelectedConceptGroupsRef.current = selectedConceptGroups;
    }
  }, [open, selectedConceptGroupsChanged, selectedConceptGroups]);

  const hasSelectionChanged = (
    current: ComboBoxDropdownListItem[],
    original: ComboBoxDropdownListItem[]
  ): boolean => {
    if (current.length !== original.length) return true;

    const currentValues = current.map((item) => item.value).sort();
    const originalValues = original.map((item) => item.value).sort();

    return !currentValues.every((value, index) => value === originalValues[index]);
  };
  const handleComboBoxChange = useCallback(
    (event: React.SyntheticEvent, value: DropdownListItem | DropdownListItem[] | null) => {
      if (Array.isArray(value)) {
        if (value.length <= 10) {
          setLocalSelectedGroups(value as ComboBoxDropdownListItem[]);
        }
      } else if (value === null) {
        setLocalSelectedGroups([]);
      }
    },
    []
  );

  const handleApply = useCallback(() => {
    const conceptGroups: ConceptGroup[] = localSelectedGroups.map((item) => ({
      conceptGroupId: item.value,
      conceptGroupName: String(item.displayValue),
      domains: '',
      conceptCount: 0,
    }));
    onApplySelection(conceptGroups);
    onClose();
  }, [localSelectedGroups, onApplySelection, onClose]);

  const handleCancel = useCallback(() => {
    const selectedWithKeys = selectedConceptGroups.map((item) => ({
      ...item,
      key: item.value,
    }));
    setLocalSelectedGroups(selectedWithKeys);
    onClose();
  }, [selectedConceptGroups, onClose]);

  const handleActionButtonClick = useCallback((value: string) => {
    setSelectedConceptGroupForDetails(value);
  }, []);

  const handleCloseConceptGroupDetails = useCallback(() => {
    setSelectedConceptGroupForDetails(undefined);
  }, []);

  const canApply =
    localSelectedGroups.length > 0 &&
    localSelectedGroups.length <= 10 &&
    hasSelectionChanged(localSelectedGroups, originalSelectedGroups);

  const domainDisplayName = useMemo(() => {
    switch (domain) {
      case 'ccCondition':
        return intl.formatMessage({
          id: 'projects.cohortBuilder.cohortCharacteristics.domain.conditions',
        });
      case 'ccDrug':
        return intl.formatMessage({
          id: 'projects.cohortBuilder.cohortCharacteristics.domain.drugs',
        });
      case 'ccMeasurement':
        return intl.formatMessage({
          id: 'projects.cohortBuilder.cohortCharacteristics.domain.measurements',
        });
      case 'ccObservation':
        return intl.formatMessage({
          id: 'projects.cohortBuilder.cohortCharacteristics.domain.observations',
        });
      case 'ccProcedure':
        return intl.formatMessage({
          id: 'projects.cohortBuilder.cohortCharacteristics.domain.procedures',
        });
      case 'ccVisit':
        return intl.formatMessage({
          id: 'projects.cohortBuilder.cohortCharacteristics.domain.visits',
        });
      default:
        return 'items';
    }
  }, [domain, intl]);

  const comboBoxOptions = useMemo(() => {
    return availableConceptGroups.map((item) => ({
      ...item,
      key: item.value,
    })) as ComboBoxDropdownListItem[];
  }, [availableConceptGroups]);

  return {
    localSelectedGroups,
    selectedConceptGroupForDetails,
    canApply,
    domainDisplayName,
    comboBoxOptions,
    handleComboBoxChange,
    handleApply,
    handleCancel,
    handleActionButtonClick,
    handleCloseConceptGroupDetails,
  };
};
